import { useEffect, useRef } from 'react';
import { useEditor, useEditorState, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TableKit } from '@tiptap/extension-table';
import { Icon } from '../../components/Icon.tsx';
import { dv } from '../../i18n/dv.ts';
import { useScrollMemory, EDITOR_SCROLL_PREFIX } from './useScrollMemory.ts';

// NOTE on Underline: TipTap v3's StarterKit already bundles the Underline mark
// (v2 did not). @tiptap/extension-underline is a declared dependency for
// clarity, but registering it a second time here would collide with
// StarterKit's own copy (duplicate-extension warning, last one wins), so
// `toggleUnderline` below comes from StarterKit. Same call as WapDocs makes.

interface ToolbarButtonProps {
  active?: boolean;
  label: string;
  icon: string;
  onClick: () => void;
}

function ToolbarButton({ active, label, icon, onClick }: ToolbarButtonProps): JSX.Element {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      // Keep the document selection: a mousedown on the button would blur the
      // editor first, and the command would have nothing to apply to.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={[
        'inline-flex size-9 shrink-0 items-center justify-center rounded-full transition-colors',
        active
          ? 'bg-[hsl(var(--primary)/0.14)] text-primary'
          : 'text-muted-foreground hover:bg-[hsl(var(--primary)/0.06)] hover:text-foreground',
      ].join(' ')}
    >
      <Icon name={icon} size={20} fill={active} />
    </button>
  );
}

function Divider(): JSX.Element {
  return <span className="mx-1 h-5 w-px shrink-0 bg-border" aria-hidden />;
}

function Toolbar({ editor }: { editor: Editor }): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-border px-2 py-1.5">
      <ToolbarButton
        label={dv.notes.bold} icon="format_bold"
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolbarButton
        label={dv.notes.italic} icon="format_italic"
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <ToolbarButton
        label={dv.notes.underline} icon="format_underlined"
        active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      />

      <Divider />

      <ToolbarButton
        label={dv.notes.heading2} icon="format_h2"
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      />
      <ToolbarButton
        label={dv.notes.heading3} icon="format_h3"
        active={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      />

      <Divider />

      <ToolbarButton
        label={dv.notes.bulletList} icon="format_list_bulleted"
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <ToolbarButton
        label={dv.notes.orderedList} icon="format_list_numbered"
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />

      <Divider />

      <ToolbarButton
        label={dv.notes.alignRight} icon="format_align_right"
        active={editor.isActive({ textAlign: 'right' })}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
      />
      <ToolbarButton
        label={dv.notes.alignCenter} icon="format_align_center"
        active={editor.isActive({ textAlign: 'center' })}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
      />
      {/* No align-LEFT button. WapDocs' shipped label for it contains a Sinhala
          character (U+0DB8) inside otherwise-Thaana text — a corrupted string we
          will not propagate — and guessing the correct Dhivehi is not something
          this codebase does. Restore the button once the real wording is
          supplied; the extension and the sanitizer already permit it. */}
      <ToolbarButton
        label={dv.notes.alignJustify} icon="format_align_justify"
        active={editor.isActive({ textAlign: 'justify' })}
        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
      />

      <Divider />

      <ToolbarButton
        label={dv.notes.table} icon="table_chart"
        active={editor.isActive('table')}
        onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
      />
    </div>
  );
}

/**
 * Row/column actions, shown only while the caret is actually inside a table.
 * They are text rather than icons on purpose: Material Symbols has
 * `add_row_below` and `add_column_right` but NO delete-row or delete-column
 * glyph, and reusing the add icons for the delete actions would make the two
 * destructive buttons the ambiguous ones. Verified against the real icon
 * metadata rather than assumed.
 */
function TableToolbar({ editor }: { editor: Editor }): JSX.Element | null {
  // TipTap v3 defaults `shouldRerenderOnTransaction` to FALSE, so reading
  // `editor.isActive('table')` straight in render would only be re-evaluated
  // when something else re-rendered this component — the bar would fail to
  // appear when you simply click into an existing table without typing.
  // useEditorState subscribes to just this one derived value.
  const inTable = useEditorState({ editor, selector: ({ editor: e }) => e.isActive('table') });
  if (!inTable) return null;

  const actions: { label: string; run: () => void; danger?: boolean }[] = [
    { label: dv.notes.tableAddRow, run: () => editor.chain().focus().addRowAfter().run() },
    { label: dv.notes.tableAddColumn, run: () => editor.chain().focus().addColumnAfter().run() },
    { label: dv.notes.tableDeleteRow, run: () => editor.chain().focus().deleteRow().run(), danger: true },
    { label: dv.notes.tableDeleteColumn, run: () => editor.chain().focus().deleteColumn().run(), danger: true },
    { label: dv.notes.tableDelete, run: () => editor.chain().focus().deleteTable().run(), danger: true },
  ];

  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-border bg-[hsl(var(--chrome))] px-2 py-1.5">
      {actions.map((a) => (
        <button
          key={a.label}
          type="button"
          // Same reason as the toolbar buttons: a mousedown would blur the
          // editor and the command would have no selection to act on.
          onMouseDown={(e) => e.preventDefault()}
          onClick={a.run}
          className={[
            'rounded-full px-3 py-1 text-xs font-medium transition-colors',
            a.danger
              ? 'text-muted-foreground hover:bg-[hsl(var(--destructive)/0.1)] hover:text-[hsl(var(--destructive))]'
              : 'text-muted-foreground hover:bg-[hsl(var(--primary)/0.08)] hover:text-foreground',
          ].join(' ')}
        >
          {a.label}
        </button>
      ))}
    </div>
  );
}

interface NoteEditorProps {
  /** Seeds the editor ONCE, on mount. Later changes to it are ignored. */
  initialHtml: string;
  onChange: (html: string) => void;
  /**
   * Identifies WHICH document this is, so its scroll position can be put back
   * when you return to it. Omit to disable scroll restoration entirely.
   */
  scrollKey?: string;
}

/**
 * The notepad's rich-text surface. Basic formatting only — headings, lists,
 * bold/italic/underline and alignment — which is exactly the sanitizer's
 * allowlist on the server, so nothing typed here is silently stripped on save.
 *
 * The content is seeded ONCE, from `initialHtml`, and TipTap owns the document
 * afterwards; `onChange` streams HTML into the same debounced save the textarea
 * used to feed. The page mounts this only once its note has genuinely loaded,
 * and remounts it (via `key`) for a different meeting or after a regenerate —
 * so this component never has to push content into a live editor, which is the
 * one operation that could fire an update and save over the real note.
 */
export function NoteEditor({ initialHtml, onChange, scrollKey }: NoteEditorProps): JSX.Element {
  const initialHtmlRef = useRef(initialHtml);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Belt-and-braces against TipTap's own initial parse/normalisation emitting
  // an update: nothing is reported upward until after mount has settled.
  const ready = useRef(false);

  const editor = useEditor({
    content: initialHtmlRef.current,
    extensions: [
      // h4 MUST be in the schema even though the toolbar only offers h2/h3:
      // the generated chair script marks every agenda item as an h4, and a
      // level TipTap does not know is parsed as a plain paragraph — which
      // autosave would then persist, destroying those headings for good.
      StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
      TextAlign.configure({ types: ['heading', 'paragraph'], defaultAlignment: 'right' }),
      // Tables must be in the schema for the SAME reason h4 is: a tag TipTap
      // cannot parse is flattened to paragraphs, and the debounced autosave
      // then persists the flattened document. Without this, pasting a table
      // from Word would destroy it on the next keystroke. The server's
      // sanitizer allowlist has to permit exactly these tags too.
      // renderWrapper puts the table in a .tableWrapper div, which is what
      // makes a wide table scroll inside the page instead of stretching it.
      TableKit.configure({ table: { resizable: true, renderWrapper: true } }),
    ],
    editorProps: {
      attributes: { dir: 'rtl', class: 'note-prose' },
    },
    onUpdate: ({ editor: ed }) => {
      if (!ready.current) return;
      onChangeRef.current(ed.getHTML());
    },
  });

  useEffect(() => { ready.current = true; }, []);
  useEffect(() => () => { editor?.destroy(); }, [editor]);

  // The scrolling element is EditorContent's own div (it carries
  // `overflow-y-auto`), which the component forwards a ref to — so this hooks
  // the real scroll container without changing the markup or the styles.
  const scrollEl = useRef<HTMLDivElement | null>(null);
  useScrollMemory(scrollEl, editor && scrollKey ? scrollKey : undefined, EDITOR_SCROLL_PREFIX);

  if (!editor) return <div className="min-h-[18rem] rounded-2xl border border-border" />;

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-background">
      <Toolbar editor={editor} />
      <TableToolbar editor={editor} />
      <EditorContent
        ref={scrollEl}
        editor={editor}
        className="note-editor max-h-[36rem] min-h-[18rem] overflow-y-auto px-4 py-3"
      />
    </div>
  );
}
